import { formatPercent } from '../../utils/percent';
import {
  BODY_FONT,
  canvas,
  canvasDefs,
  escapeXml,
  giraffe,
  HEAD_FONT,
  headerSparkles,
  LIGHT_SKIN,
  planAndBrandFooter,
  type Skin,
  statTile,
  surface,
  trophy,
  twoToneTitle,
  wrapText,
} from './cardSkin';

interface CourseCompletionCardProps {
  lessonsCompleted?: number;
  totalLessons?: number;
  totalPoints?: number;
  daysToComplete?: number;
  /** One score per module, in course order (the server's `pathScores`). */
  pathScores?: number[];
  /**
   * The module titles, in the same order as `pathScores`. When missing, or
   * when the count does not match, the rows say "Module 1", "Module 2" …
   * rather than pair a score with the wrong name.
   */
  moduleNames?: string[];
  averageScore?: number;
  isPremium?: boolean;
}

/**
 * CourseCompletionCard
 *
 * The shareable image for finishing the whole course, drawn as SVG so the
 * Share button can export it (CelebrationModal -> svgToBase64Png) at the
 * same 1080 x 1350 (4:5) it shows on screen.
 *
 * The new skin (variant A, David 2026-10-24): the redesign tokens
 * (design-system/tokens.css) as literal colours, a white surface on the warm
 * canvas, three stat tiles, and the module scores as a compact strip of bars,
 * two columns once there are more than four modules, so eight modules with
 * their names fit the card at phone width.
 *
 * Fonts: an SVG drawn into a canvas cannot use the page's web fonts, so the
 * Share path embeds Inter and Space Grotesk into the exported copy
 * (utils/svg-fonts.ts); on screen they come from the page.
 */

/** The white surface, and the area inside it the module strip may use. */
const CARD_SURFACE = { x: 60, y: 262, width: 960, height: 794 } as const;
const STRIP = {
  left: 110,
  right: 970,
  top: 760,
  /** The strip ends here, 26 units above the surface's bottom edge. */
  bottom: CARD_SURFACE.y + CARD_SURFACE.height - 26,
  columnGap: 60,
  maxRowHeight: 72,
};
/** More modules than this go into two columns. */
const ONE_COLUMN_MAX = 4;
/** The longest name a half-width column holds at the label size. */
const NAME_MAX_CHARS = 22;

interface ModuleRowLayout {
  index: number;
  col: number;
  row: number;
  x: number;
  y: number;
  width: number;
  rowHeight: number;
}

/**
 * Where each module's row goes. Up to four modules: one full-width column.
 * More: two columns, filled top to bottom then left to right, so the course
 * order reads down the first column first. The row height shrinks when the
 * rows would run past the strip's bottom, so a longer course still fits.
 */
function moduleStripLayout(count: number): ModuleRowLayout[] {
  if (count <= 0) return [];
  const columns = count > ONE_COLUMN_MAX ? 2 : 1;
  const rows = Math.ceil(count / columns);
  const rowHeight = Math.min(STRIP.maxRowHeight, (STRIP.bottom - STRIP.top) / rows);
  const width = (STRIP.right - STRIP.left - (columns - 1) * STRIP.columnGap) / columns;
  return Array.from({ length: count }, (_, index) => {
    const col = Math.floor(index / rows);
    const row = index % rows;
    return {
      index,
      col,
      row,
      x: STRIP.left + col * (width + STRIP.columnGap),
      y: STRIP.top + row * rowHeight,
      width,
      rowHeight,
    };
  });
}

/** The module's label: its title when the titles line up with the scores. */
function moduleLabel(names: string[] | undefined, count: number, index: number): string {
  const name = names && names.length === count ? names[index]?.trim() : '';
  const label = name || `Module ${index + 1}`;
  if (label.length <= NAME_MAX_CHARS) return label;
  return `${label.slice(0, NAME_MAX_CHARS - 1).trimEnd()}…`;
}

function moduleStrip(scores: number[], names: string[] | undefined, C: Skin): string {
  return moduleStripLayout(scores.length)
    .map(({ index, col, row, x, y, width, rowHeight }) => {
      const score = scores[index];
      const fraction = Number.isFinite(score) ? Math.min(100, Math.max(0, score)) / 100 : 0;
      // Label and bar scale with the row when a long course shrinks it.
      const k = Math.min(1, rowHeight / STRIP.maxRowHeight);
      const fontSize = Math.round(28 * k);
      const barY = y + 40 * k;
      const barH = Math.max(6, 14 * k);
      return `
      <g data-module-row="" data-col="${col}" data-row="${row}">
        <text data-module-name="" x="${x}" y="${y + 26 * k}" font-family="${BODY_FONT}" font-weight="600" font-size="${fontSize}" fill="${C.navy}">${escapeXml(moduleLabel(names, scores.length, index))}</text>
        <text data-module-score="" x="${x + width}" y="${y + 26 * k}" font-family="${HEAD_FONT}" font-weight="700" font-size="${fontSize}" fill="${C.orangeText}" text-anchor="end">${formatPercent(score)}</text>
        <rect data-bar-track="" x="${x}" y="${barY}" width="${width}" height="${barH}" rx="${barH / 2}" fill="${C.muted}"/>
        <rect data-bar-fill="" x="${x}" y="${barY}" width="${width * fraction}" height="${barH}" rx="${barH / 2}" fill="url(#eeCourseBar)"/>
      </g>`;
    })
    .join('');
}

/**
 * The card's accessible name (b-022): its text is drawn into the SVG through
 * dangerouslySetInnerHTML, which a screen reader reads as fragments in draw
 * order. The same defaults as courseCardSvg, so the name says what is drawn.
 */
export function courseCardLabel({
  lessonsCompleted = 60,
  totalLessons = 60,
  totalPoints = 2450,
  averageScore = 88,
}: CourseCompletionCardProps): string {
  return `Course complete: ${lessonsCompleted} of ${totalLessons} lessons, ${totalPoints.toLocaleString()} points, average ${formatPercent(averageScore)}`;
}

export function courseCardSvg(
  {
    lessonsCompleted = 60,
    totalLessons = 60,
    totalPoints = 2450,
    daysToComplete = 45,
    pathScores = [85, 92, 78, 88, 95],
    moduleNames,
    averageScore = 88,
    isPremium = false, // kept for backwards compatibility, represents any paid plan
  }: CourseCompletionCardProps,
  C: Skin = LIGHT_SKIN,
): string {
  // Custom completion message (a colon where it had an em dash: no em dashes in copy)
  const completionMessage =
    'You\'ve learned a framework that frees you from seeing the world through the narrow window of right and wrong. Ahead lies the true "Marshall art": the lifelong practice of mastering it.';
  const messageLines = wrapText(completionMessage, 68);
  const surfaceBottom = CARD_SURFACE.y + CARD_SURFACE.height;
  const messageTop = surfaceBottom + 58;

  const svgContent = `
    <defs>${canvasDefs('eeCourse', C)}
      <linearGradient id="eeCourseBar" x1="0" x2="1" y1="0" y2="0">
        <stop offset="0%" stop-color="${C.orangeLight}"/><stop offset="100%" stop-color="${C.orange}"/>
      </linearGradient>
    </defs>

    <!-- Canvas with two soft glows -->${canvas('eeCourse', C)}

    <!-- Trophy and sparkles, calmer -->
    ${headerSparkles(C)}
    ${trophy(540, 100, C)}

    ${twoToneTitle('COURSE', 'COMPLETE!', 228, C)}

    <!-- The white surface -->${surface(CARD_SURFACE.x, CARD_SURFACE.y, CARD_SURFACE.width, CARD_SURFACE.height, 'eeCourse', C)}

    <!-- Cute graduated giraffe with diploma image -->
    ${giraffe(470, CARD_SURFACE.y + 18, 140)}

    <text x="540" y="${CARD_SURFACE.y + 196}" font-family="${BODY_FONT}" font-weight="700" font-size="25" letter-spacing="5" fill="${C.orangeText}" text-anchor="middle">YOUR ACHIEVEMENTS</text>

    ${statTile(110, CARD_SURFACE.y + 224, 'book', `${lessonsCompleted}/${totalLessons}`, 'Lessons', 'lessons', C)}
    ${statTile(405, CARD_SURFACE.y + 224, 'star', totalPoints.toLocaleString(), 'Points', 'points', C)}
    ${statTile(700, CARD_SURFACE.y + 224, 'calendar', String(daysToComplete), 'Days', 'days', C)}

    <!-- Module scores: the heading, the average, and the strip -->
    <text x="${STRIP.left}" y="${STRIP.top - 32}" font-family="${HEAD_FONT}" font-weight="700" font-size="32" fill="${C.navy}">Module Scores</text>
    <rect x="${STRIP.right - 236}" y="${STRIP.top - 72}" width="236" height="54" rx="27" fill="${C.peach}"/>
    <text x="${STRIP.right - 118}" y="${STRIP.top - 36}" font-family="${BODY_FONT}" font-weight="700" font-size="25" fill="${C.orangeText}" text-anchor="middle">Average: ${formatPercent(averageScore)}</text>
    ${moduleStrip(pathScores, moduleNames, C)}

    <!-- Completion Message -->
    <text font-family="${BODY_FONT}" font-weight="400" font-size="25" fill="${C.ink2}" text-anchor="middle">${messageLines
      .map(
        (line, index) => `<tspan x="540" y="${messageTop + index * 36}">${escapeXml(line)}</tspan>`,
      )
      .join('')}</text>

    <!-- Plan badge and brand footer -->${planAndBrandFooter(isPremium, C)}
  `;

  return svgContent;
}

/**
 * The card on screen: the light skin (the share image's) unless the modal
 * hands it the live theme's (dark mode). Share exports courseCardSvg(props,
 * LIGHT_SKIN) whatever the screen shows.
 */
function CourseCompletionCard({ skin, ...props }: CourseCompletionCardProps & { skin?: Skin }) {
  return (
    <svg
      id="shareable-course-completion-card"
      role="img"
      aria-label={courseCardLabel(props)}
      data-skin={skin && skin !== LIGHT_SKIN ? 'theme' : 'light'}
      width="100%"
      height="100%"
      viewBox="0 0 1080 1350"
      preserveAspectRatio="xMidYMid meet"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      style={{ display: 'block' }}
      dangerouslySetInnerHTML={{ __html: courseCardSvg(props, skin) }}
    />
  );
}

export default CourseCompletionCard;
